import React, { useEffect, useMemo, useState } from "react";
import Layout from "../Component/Layout";
import { ORDER_API } from "../apiConfig";

// ── Config ───────────────────────────────────────────────────────────────
// AuditCalender lives in order-service alongside sales_order (same DB
// connection, since it queries the sales_order table directly) — so this
// goes through ORDER_API, not PURCHASE_API.
const API_BASE = `${ORDER_API}/audit-calendar`;

// NOTE ON TIME: Sales Order now only captures a proposed/confirmed *date* —
// no time. Every inspection is treated as an all-day event (the customer
// can show up any time that day), so this whole component works purely in
// days: no hour grid, no start/end, no duration math. Only the *view*
// (Month / Week / Day) changes how many days are on screen at once.
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
const DOW_SHORT = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const DOW_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MAX_CHIPS_MONTH = 3; // beyond this, a day cell in Month view collapses into "+N more"

const pad2 = (n) => String(n).padStart(2, "0");
const toDateKey = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;
const dateKeyOf = (date) => toDateKey(date.getFullYear(), date.getMonth() + 1, date.getDate());
const addDays = (date, n) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + n);
  return d;
};
const startOfWeek = (date) => addDays(date, -date.getDay()); // back up to Sunday
const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// Person Responsible is always the currently logged-in user — never typed
// in — same lookup SalesOrder.js uses against the "user" key in
// localStorage, kept in sync here so both screens resolve the same name.
// Prefer the actual user id (e.g. "maithili") so it's stored consistently
// with Purchase Request / Goods Receipt / Purchase Order (created_by,
// approver_id, etc. all store userId, not a name or email).
const getCurrentUser = () => {
  const userObj = localStorage.getItem("user");
  if (userObj) {
    try {
      const parsedUser = JSON.parse(userObj);
      if (parsedUser.userId) return parsedUser.userId;
      const fullName = `${parsedUser.firstName || ''} ${parsedUser.lastName || ''}`.trim();
      if (fullName) return fullName;
      if (parsedUser.name) return parsedUser.name;
      if (parsedUser.username) return parsedUser.username;
      if (parsedUser.email) return parsedUser.email;
    } catch (e) { }
  }
  const keys = ["userId", "userName", "username", "user_name", "name", "fullName", "email"];
  for (const key of keys) {
    const val = localStorage.getItem(key);
    if (val && val !== "undefined" && val !== "null") return val;
  }
  return "User";
};

function AuditCalendar() {
  const today = new Date();

  // Single source of truth for "where the calendar is looking" — Month view
  // reads its month/year off this, Week view reads the Sunday-start week
  // containing it, Day view reads the exact day. Prev/Next/Today all just
  // move this one date around.
  const [anchorDate, setAnchorDate] = useState(() => new Date());
  const [viewMode, setViewMode] = useState("month"); // "month" | "week" | "day"

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);

  // ── Drag-and-drop reschedule state ──────────────────────────────────────
  const [draggedSoId, setDraggedSoId] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [savingSoId, setSavingSoId] = useState(null);

  // ── Event-detail modal: manual edit of the proposed date, confirm, and
  // the note/assigned-to fields ───────────────────────────────────────────
  const [editDate, setEditDate] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [confirming, setConfirming] = useState(false);
  // Modal opens read-only; "Edit" unlocks Proposed Date and Note.
  // Person Responsible is never manually edited — it's always auto-set to
  // the logged-in user whenever Save or Confirm is clicked. The single
  // "Save" button below writes all of it in one go and locks the modal
  // back down. "Confirm" is separate and unaffected.
  const [isEditingModal, setIsEditingModal] = useState(false);
  const [savingAll, setSavingAll] = useState(false);

  // ── Day overflow / "+N more" chooser: Month view caps how many chips a
  // day cell shows; clicking the overflow (or an ambiguous multi-SO chip)
  // opens this small picker instead of guessing which one. ───────────────
  const [groupChooser, setGroupChooser] = useState(null); // { label, events } | null

  // Sidebar starts closed on this page; Layout/Navbar's own toggle handles
  // opening/closing from here on (no page-local sidebar state needed).

  const month = anchorDate.getMonth() + 1;
  const year = anchorDate.getFullYear();
  const weekStart = useMemo(() => startOfWeek(anchorDate), [anchorDate]);

  // ── Fetch events whenever the view or the visible range changes ────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    let url;
    if (viewMode === "month") {
      url = `${API_BASE}/events?month=${month}&year=${year}`;
    } else {
      const rangeStart = viewMode === "week" ? weekStart : anchorDate;
      const rangeEnd = viewMode === "week" ? addDays(weekStart, 6) : anchorDate;
      url = `${API_BASE}/events?start=${dateKeyOf(rangeStart)}&end=${dateKeyOf(rangeEnd)}`;
    }

    fetch(url)
      .then(async (res) => {
        const contentType = res.headers.get("content-type") || "";
        if (!contentType.includes("application/json")) {
          throw new Error(
            `Got a non-JSON response from ${API_BASE}/events — check that API_BASE points at the backend (proxy/port/prefix), not the frontend dev server.`
          );
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (data.success === false) throw new Error(data.message || "Failed to load events");
        setEvents(data.events || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load audit calendar events");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, month, year, weekStart.getTime(), anchorDate.getTime()]);

  // Days making up the Month grid — includes the leading/trailing days from
  // the adjacent months needed to fill out full weeks (Sun → Sat rows), same
  // as a normal calendar app. Those spillover days are still valid drop
  // targets (dragging July 31st's inspection onto Aug 1st should just work),
  // they just render dimmed and won't have events loaded for them until the
  // view actually moves onto that month.
  const monthGridDays = useMemo(() => {
    const firstOfMonth = new Date(year, month - 1, 1);
    const lastOfMonth = new Date(year, month, 0);
    const gridStart = startOfWeek(firstOfMonth);
    const gridEnd = addDays(startOfWeek(lastOfMonth), 6);
    const days = [];
    let cur = gridStart;
    while (cur <= gridEnd) {
      days.push(cur);
      cur = addDays(cur, 1);
    }
    return days;
  }, [month, year]);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  // Group events by the calendar date they're proposed on
  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((ev) => {
      if (!map[ev.date]) map[ev.date] = [];
      map[ev.date].push(ev);
    });
    return map;
  }, [events]);

  // ── Drag-and-drop reschedule (date only — no time to compute anymore) ──
  const handleEventDragStart = (ev, e) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(ev.soId));
    setDraggedSoId(ev.soId);
  };

  const handleEventDragEnd = () => {
    setDraggedSoId(null);
    setDragOverDate(null);
  };

  const handleDayDragOver = (e, dateKey) => {
    e.preventDefault(); // required to allow a drop
    e.dataTransfer.dropEffect = "move";
    if (dragOverDate !== dateKey) setDragOverDate(dateKey);
  };

  const handleDayDragLeave = (dateKey) => {
    setDragOverDate((cur) => (cur === dateKey ? null : cur));
  };

  const handleDayDrop = async (e, dateKey) => {
    e.preventDefault();
    const soId = e.dataTransfer.getData("text/plain") || draggedSoId;
    setDragOverDate(null);
    setDraggedSoId(null);
    if (!soId) return;

    const draggedEvent = events.find((ev) => String(ev.soId) === String(soId));
    if (!draggedEvent) return;

    // Nothing actually changed — skip the round trip.
    if (draggedEvent.date === dateKey) return;

    // Optimistic update: move the chip immediately. Dragging is itself an
    // action on this (Quality) calendar, so it also stamps Person
    // Responsible with whoever is logged in and dragging it.
    const draggedBy = getCurrentUser();
    const updatedEvent = {
      ...draggedEvent,
      date: dateKey,
      proposedSource: "audit_calendar",
      department: "Quality",
      assignedTo: draggedBy
    };

    const prevEvents = events;
    setEvents((prev) => prev.map((ev) => (String(ev.soId) === String(soId) ? updatedEvent : ev)));
    setSelectedEvent((prev) => (prev && String(prev.soId) === String(soId) ? updatedEvent : prev));
    setSavingSoId(soId);

    try {
      const res = await fetch(`${API_BASE}/reschedule/${soId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: dateKey, assignedTo: draggedBy })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to reschedule inspection");
      }
    } catch (err) {
      // Roll back on failure so the UI matches what's actually in the DB.
      setEvents(prevEvents);
      setSelectedEvent((prev) => (prev && String(prev.soId) === String(soId) ? draggedEvent : prev));
      setError(err.message || "Failed to reschedule inspection");
    } finally {
      setSavingSoId(null);
    }
  };

  // ── Open/close the event-detail modal, seeding the edit/note/assignedTo
  // drafts from whatever is currently on the event ───────────────────────
  const openEvent = (ev) => {
    setSelectedEvent(ev);
    setEditDate(ev.date);
    setNoteDraft(ev.note || "");
    setGroupChooser(null);
    setIsEditingModal(false);
  };

  const closeEvent = () => {
    setSelectedEvent(null);
    setConfirming(false);
    setIsEditingModal(false);
  };

  // ── "Save" — the modal's only save button now. Unlocked fields are
  // Proposed Date and Note; this writes all of it in one go (proposed date
  // via reschedule, if it actually changed; note/assignedTo via /confirm
  // without confirmedDate so it doesn't touch the confirmed column), then
  // locks the modal back to read-only. Person Responsible always gets set
  // to the logged-in user, alongside whatever else this Save writes.
  // "Confirm" (top-right) stays completely separate.
  const handleSaveAll = async () => {
    if (!selectedEvent) return;
    setSavingAll(true);
    setError("");
    // Person Responsible is never typed — whoever is logged in and hits
    // Save right now is who gets stamped on this action.
    const savedBy = getCurrentUser();
    try {
      let updatedEvent = { ...selectedEvent };

      const dateChanged = editDate && editDate !== selectedEvent.date;

      if (dateChanged) {
        const res = await fetch(`${API_BASE}/reschedule/${selectedEvent.soId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ date: editDate, assignedTo: savedBy })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.success === false) {
          throw new Error(data.message || "Failed to update proposed schedule");
        }

        updatedEvent = {
          ...updatedEvent,
          date: editDate,
          proposedSource: "audit_calendar",
          department: "Quality"
        };
      }

      const res2 = await fetch(`${API_BASE}/confirm/${selectedEvent.soId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: noteDraft, assignedTo: savedBy })
      });
      const data2 = await res2.json().catch(() => ({}));
      if (!res2.ok || data2.success === false) {
        throw new Error(data2.message || "Failed to save note / assigned to");
      }
      updatedEvent = { ...updatedEvent, note: noteDraft, assignedTo: savedBy };

      setEvents((prev) => prev.map((e) => (String(e.soId) === String(updatedEvent.soId) ? updatedEvent : e)));
      setSelectedEvent(updatedEvent);
      setIsEditingModal(false);
    } catch (err) {
      setError(err.message || "Failed to save");
    } finally {
      setSavingAll(false);
    }
  };

  // ── "Confirm" button at the top of the modal — writes whatever date the
  // Proposed field is currently showing (a freshly-typed edit, or the
  // original proposal if untouched) into the confirmed column, along with
  // the Note draft and the logged-in user as Person Responsible. ─────────
  const handleConfirmInspection = async () => {
    if (!selectedEvent) return;
    setConfirming(true);
    setError("");
    const confirmedDate = editDate;
    const confirmedBy = getCurrentUser();
    try {
      const res = await fetch(`${API_BASE}/confirm/${selectedEvent.soId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          confirmedDate,
          note: noteDraft,
          assignedTo: confirmedBy
        })
      });
      const rawText = await res.text();
      let data = {};
      try { data = rawText ? JSON.parse(rawText) : {}; } catch { /* not JSON — see fallback below */ }

      if (!res.ok || data.success === false) {
        // If the server didn't send JSON at all (route not registered,
        // proxy 404, unhandled 500, etc.) surface the raw status/body
        // instead of a generic message — much faster to debug than a
        // blanket "Failed to confirm inspection".
        const fallback = rawText && !data.message
          ? `${res.status} ${res.statusText}: ${rawText.slice(0, 200)}`
          : "Failed to confirm inspection";
        throw new Error(data.message || fallback);
      }

      const updatedEvent = {
        ...selectedEvent,
        confirmedDate,
        note: noteDraft,
        assignedTo: confirmedBy
      };
      setEvents((prev) => prev.map((e) => (String(e.soId) === String(updatedEvent.soId) ? updatedEvent : e)));
      setSelectedEvent(updatedEvent);
    } catch (err) {
      setError(err.message || "Failed to confirm inspection");
    } finally {
      setConfirming(false);
    }
  };

  // ── Navigation — Prev/Next/Today all move the single anchorDate. What a
  // "step" means depends on which view is active. ───────────────────────
  const goToPrev = () => {
    setAnchorDate((d) => {
      if (viewMode === "month") return new Date(d.getFullYear(), d.getMonth() - 1, 1);
      if (viewMode === "week") return addDays(d, -7);
      return addDays(d, -1);
    });
  };
  const goToNext = () => {
    setAnchorDate((d) => {
      if (viewMode === "month") return new Date(d.getFullYear(), d.getMonth() + 1, 1);
      if (viewMode === "week") return addDays(d, 7);
      return addDays(d, 1);
    });
  };
  const goToToday = () => setAnchorDate(new Date());

  const yearOptions = useMemo(() => {
    const base = today.getFullYear();
    return Array.from({ length: 6 }, (_, i) => base - 2 + i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMonthSelect = (m) => setAnchorDate(new Date(year, m - 1, Math.min(anchorDate.getDate(), 28)));
  const handleYearSelect = (y) => setAnchorDate(new Date(y, month - 1, Math.min(anchorDate.getDate(), 28)));
  const handleJumpToDate = (dateStr) => {
    if (!dateStr) return;
    const [y, m, d] = dateStr.split("-").map(Number);
    setAnchorDate(new Date(y, m - 1, d));
  };

  // Header label — its format depends on the active view.
  const rangeLabel = useMemo(() => {
    if (viewMode === "month") return `${MONTH_NAMES[month - 1]} ${year}`;
    if (viewMode === "day") {
      return `${DOW_LONG[anchorDate.getDay()]}, ${MONTH_NAMES[anchorDate.getMonth()]} ${anchorDate.getDate()}, ${anchorDate.getFullYear()}`;
    }
    const weekEnd = addDays(weekStart, 6);
    const sameMonth = weekStart.getMonth() === weekEnd.getMonth();
    const startLabel = `${MONTH_NAMES[weekStart.getMonth()].slice(0, 3)} ${weekStart.getDate()}`;
    const endLabel = sameMonth
      ? `${weekEnd.getDate()}`
      : `${MONTH_NAMES[weekEnd.getMonth()].slice(0, 3)} ${weekEnd.getDate()}`;
    return `${startLabel} – ${endLabel}, ${weekEnd.getFullYear()}`;
  }, [viewMode, month, year, anchorDate, weekStart]);

  // ── Small shared bits: an event chip (Month/Week) and a fuller row card
  // (Day view has room to show more per inspection). ─────────────────────
  const renderChip = (ev) => {
    const isDragging = draggedSoId === ev.soId;
    const isSaving = savingSoId === ev.soId;
    const isConfirmed = !!ev.confirmedDate;
    return (
      <div
        key={ev.soId}
        className={`ac-chip ${isDragging ? "is-dragging" : ""} ${isSaving ? "is-saving" : ""} ${isConfirmed ? "is-confirmed" : ""}`}
        draggable
        onDragStart={(e) => handleEventDragStart(ev, e)}
        onDragEnd={handleEventDragEnd}
        onClick={(e) => { e.stopPropagation(); if (!isDragging) openEvent(ev); }}
        title={`${ev.soId} — ${ev.customer} (drag to reschedule)`}
      >
        <span className="so-id">{ev.soId}</span>
        <span className="customer">{ev.customer}</span>
      </div>
    );
  };

  const renderDayCard = (ev) => {
    const isDragging = draggedSoId === ev.soId;
    const isSaving = savingSoId === ev.soId;
    const isConfirmed = !!ev.confirmedDate;
    return (
      <div
        key={ev.soId}
        className={`ac-day-card ${isDragging ? "is-dragging" : ""} ${isSaving ? "is-saving" : ""} ${isConfirmed ? "is-confirmed" : ""}`}
        onClick={() => openEvent(ev)}
      >
        <div className="ac-day-card-top">
          <span className="so-id">{ev.soId}</span>
          <span className={`ac-badge ${ev.department === "Sales" ? "is-sales" : "is-quality"}`}>{ev.department}</span>
        </div>
        <div className="customer">{ev.customer}</div>
        <div className="ac-day-card-meta">
          <span>{ev.status || "—"}</span>
          {isConfirmed && <span className="ac-confirmed-tag">Confirmed</span>}
        </div>
      </div>
    );
  };

  return (
    <Layout defaultSidebarOpen={false}>
      <div className="container-fluid p-4 ac-page">
        <style>{`
          .ac-page { font-family: inherit; }
          .ac-header-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 12px;
            margin-bottom: 18px;
          }
          .ac-title { margin: 0; }
          .ac-filters {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
          }
          .ac-select, .ac-date-input {
            border: 1px solid #d0d5dd;
            border-radius: 6px;
            padding: 6px 10px;
            font-size: 14px;
            background: #fff;
          }
          .ac-view-toggle {
            display: inline-flex;
            border: 1px solid #d0d5dd;
            border-radius: 8px;
            overflow: hidden;
          }
          .ac-view-toggle button {
            border: none;
            background: #fff;
            padding: 7px 16px;
            font-size: 13.5px;
            font-weight: 600;
            color: #475467;
            cursor: pointer;
            border-right: 1px solid #d0d5dd;
          }
          .ac-view-toggle button:last-child { border-right: none; }
          .ac-view-toggle button.is-active { background: #1a73e8; color: #fff; }
          .ac-view-toggle button:hover:not(.is-active) { background: #f2f4f7; }
          .ac-today-btn {
            border: 1px solid #d0d5dd;
            background: #fff;
            border-radius: 8px;
            padding: 7px 14px;
            font-size: 13.5px;
            font-weight: 600;
            color: #344054;
            cursor: pointer;
          }
          .ac-today-btn:hover { background: #f2f4f7; }
          .ac-nav {
            display: flex;
            align-items: center;
            gap: 14px;
            justify-content: center;
          }
          .ac-nav-btn {
            border: 1px solid #d0d5dd;
            background: #fff;
            border-radius: 50%;
            width: 34px;
            height: 34px;
            font-size: 16px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .ac-nav-btn:hover { background: #f2f4f7; }
          .ac-range-label {
            font-weight: 600;
            font-size: 16px;
            min-width: 190px;
            text-align: center;
          }

          .ac-cal-wrapper {
            border: 1px solid #e8eaed;
            border-radius: 14px;
            background: #fff;
            box-shadow: 0 1px 2px rgba(0,0,0,0.04);
            overflow: hidden;
          }

          /* ── Month view: classic 7-column grid ───────────────────────── */
          .ac-dow-row {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            border-bottom: 1px solid #e8eaed;
          }
          .ac-dow-cell {
            text-align: center;
            padding: 10px 4px;
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.4px;
            color: #70757a;
            text-transform: uppercase;
          }
          .ac-month-grid {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
          }
          .ac-month-cell {
            min-height: 118px;
            border-right: 1px solid #f1f3f4;
            border-bottom: 1px solid #f1f3f4;
            padding: 6px 6px 8px;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            gap: 4px;
            transition: background 0.1s ease;
          }
          .ac-month-grid .ac-month-cell:nth-child(7n) { border-right: none; }
          .ac-month-cell.is-drop-target { background: rgba(26, 115, 232, 0.08); }
          .ac-month-cell.is-outside { background: #fafbfc; }
          .ac-month-cell.is-outside .date-badge { color: #b0b6bf; }
          .ac-date-badge-row { display: flex; justify-content: flex-end; }
          .date-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 26px;
            height: 26px;
            border-radius: 50%;
            font-size: 13px;
            color: #3c4043;
          }
          .is-today .date-badge { background: #1a73e8; color: #fff; font-weight: 600; }
          .ac-month-cell-events { display: flex; flex-direction: column; gap: 3px; flex: 1; }
          .ac-overflow-btn {
            border: none; background: none; text-align: left; padding: 1px 2px;
            font-size: 11px; font-weight: 600; color: #1a73e8; cursor: pointer;
          }
          .ac-overflow-btn:hover { text-decoration: underline; }

          /* ── Week view: 7 taller columns, no hour grid — every event is
             all-day so a column is just a stack of chips. ───────────────── */
          .ac-week-grid { display: grid; grid-template-columns: repeat(7, 1fr); }
          .ac-week-col {
            border-right: 1px solid #f1f3f4;
            min-height: 480px;
            max-height: 620px;
            overflow-y: auto;
            padding: 8px 6px;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            gap: 6px;
            transition: background 0.1s ease;
          }
          .ac-week-grid .ac-week-col:last-child { border-right: none; }
          .ac-week-col.is-drop-target { background: rgba(26, 115, 232, 0.08); }
          .ac-week-col-header {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 10px 4px;
            border-bottom: 1px solid #e8eaed;
            border-right: 1px solid #f1f3f4;
          }
          .ac-week-header-row { display: grid; grid-template-columns: repeat(7, 1fr); }
          .ac-week-header-row .ac-week-col-header:last-child { border-right: none; }
          .ac-week-col-header .dow { font-size: 11px; color: #70757a; letter-spacing: 0.3px; text-transform: uppercase; margin-bottom: 4px; }
          .ac-week-col-empty { font-size: 11.5px; color: #b0b6bf; text-align: center; margin-top: 18px; }

          /* ── Day view: single column of fuller cards ─────────────────── */
          .ac-day-view { padding: 18px; min-height: 300px; max-height: 620px; overflow-y: auto; }
          .ac-day-view-empty { text-align: center; color: #98a2b3; padding: 60px 0; font-size: 14px; }
          .ac-day-card {
            background: linear-gradient(135deg, #4f8ef7 0%, #2f6fed 55%, #1d5fe0 100%);
            color: #fff;
            border-radius: 10px;
            border-left: 4px solid #123f9e;
            padding: 12px 16px;
            margin-bottom: 10px;
            cursor: pointer;
            box-shadow: 0 2px 6px rgba(29, 95, 224, 0.3);
            transition: transform 0.15s ease, box-shadow 0.15s ease;
          }
          .ac-day-card:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(18, 63, 158, 0.4); }
          .ac-day-card.is-confirmed {
            background: linear-gradient(135deg, #ffd54f 0%, #ffc107 55%, #ffb300 100%);
            border-left-color: #b28704;
            color: #3c2f00;
          }
          .ac-day-card.is-dragging { opacity: 0.4; }
          .ac-day-card.is-saving { opacity: 0.7; pointer-events: none; }
          .ac-day-card-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
          .ac-day-card .so-id { font-weight: 700; font-size: 14px; }
          .ac-day-card .customer { font-size: 13px; opacity: 0.9; margin-bottom: 6px; }
          .ac-day-card-meta { display: flex; align-items: center; gap: 10px; font-size: 12px; }
          .ac-badge {
            font-size: 10.5px; font-weight: 700; padding: 2px 8px; border-radius: 20px;
            background: rgba(255,255,255,0.25); text-transform: uppercase; letter-spacing: 0.3px;
          }
          .ac-day-card.is-confirmed .ac-badge { background: rgba(0,0,0,0.12); }
          .ac-confirmed-tag { font-weight: 700; }

          /* ── Shared event chip (Month + Week) ────────────────────────── */
          .ac-chip {
            background: linear-gradient(135deg, #4f8ef7 0%, #2f6fed 55%, #1d5fe0 100%);
            color: #fff;
            border-radius: 6px;
            border-left: 3px solid #123f9e;
            padding: 3px 7px;
            font-size: 11px;
            line-height: 1.35;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            cursor: grab;
            box-shadow: 0 1px 3px rgba(29, 95, 224, 0.3);
            transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
          }
          .ac-chip:hover { box-shadow: 0 3px 8px rgba(18, 63, 158, 0.4); transform: translateY(-1px); }
          .ac-chip:active { cursor: grabbing; }
          .ac-chip.is-dragging { opacity: 0.35; }
          .ac-chip.is-saving { opacity: 0.7; pointer-events: none; }
          .ac-chip.is-confirmed {
            background: linear-gradient(135deg, #ffd54f 0%, #ffc107 55%, #ffb300 100%);
            border-left-color: #b28704;
          }
          .ac-chip.is-confirmed .customer { color: rgba(0,0,0,0.65); }
          .ac-chip .so-id { font-weight: 700; margin-right: 4px; }
          .ac-chip .customer { font-size: 10.5px; color: rgba(255,255,255,0.85); }

          .ac-legend { margin-top: 10px; font-size: 12px; color: #667085; }
          .ac-modal-backdrop {
            position: fixed; inset: 0; background: rgba(16,24,40,0.45);
            display: flex; align-items: center; justify-content: center; z-index: 1000;
          }
          .ac-modal {
            background: #fff; border-radius: 14px; padding: 30px 34px;
            width: 620px; max-width: 92vw; max-height: 86vh; overflow-y: auto;
            box-shadow: 0 20px 50px rgba(16,24,40,0.28);
          }
          .ac-modal h4 { margin: 0 0 18px; font-size: 20px; }
          .ac-modal-row { display: flex; justify-content: space-between; padding: 9px 0; font-size: 15px; border-bottom: 1px solid #f1f3f4; }
          .ac-modal-row:last-of-type { border-bottom: none; }
          .ac-modal-row span:first-child { color: #667085; }
          .ac-modal-row span:last-child { font-weight: 500; text-align: right; margin-left: 16px; }
          .ac-modal-items-label {
            margin: 20px 0 8px; font-size: 12px; font-weight: 700; letter-spacing: 0.3px;
            text-transform: uppercase; color: #667085;
          }
          .ac-modal-items-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
          .ac-modal-items-table th {
            text-align: left; font-weight: 600; color: #667085; padding: 6px 8px;
            border-bottom: 1px solid #e4e7ec; white-space: nowrap;
          }
          .ac-modal-items-table td { padding: 7px 8px; border-bottom: 1px solid #f1f3f4; }
          .ac-modal-items-table th:last-child,
          .ac-modal-items-table td:last-child { text-align: right; }
          .ac-modal-close {
            margin-top: 22px; width: 100%; padding: 12px; border-radius: 8px;
            border: 1px solid #d0d5dd; background: #fff; cursor: pointer; font-size: 15px;
          }
          .ac-modal-close:hover { background: #f2f4f7; }
        `}</style>

        <div className="ac-header-row">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h2 className="ac-title">Inspection Calendar</h2>
          </div>

          <div className="ac-filters">
            <div className="ac-view-toggle">
              {["month", "week", "day"].map((v) => (
                <button
                  key={v}
                  className={viewMode === v ? "is-active" : ""}
                  onClick={() => setViewMode(v)}
                >
                  {v[0].toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
            <button className="ac-today-btn" onClick={goToToday}>Today</button>

            {viewMode === "month" ? (
              <>
                <select className="ac-select" value={year} onChange={(e) => handleYearSelect(Number(e.target.value))}>
                  {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
                <select className="ac-select" value={month} onChange={(e) => handleMonthSelect(Number(e.target.value))}>
                  {MONTH_NAMES.map((name, idx) => <option key={name} value={idx + 1}>{name}</option>)}
                </select>
              </>
            ) : (
              <input
                type="date"
                className="ac-date-input"
                value={dateKeyOf(anchorDate)}
                onChange={(e) => handleJumpToDate(e.target.value)}
              />
            )}
          </div>

          <div className="ac-nav">
            <button className="ac-nav-btn" onClick={goToPrev} title={`Previous ${viewMode}`}>‹</button>
            <div className="ac-range-label">{rangeLabel}</div>
            <button className="ac-nav-btn" onClick={goToNext} title={`Next ${viewMode}`}>›</button>
          </div>
        </div>

        {error && <div style={{ color: "#b42318", marginBottom: 10, fontSize: 14 }}>{error}</div>}

        <div className="ac-cal-wrapper">
          {/* ── MONTH VIEW ─────────────────────────────────────────────── */}
          {viewMode === "month" && (
            <>
              <div className="ac-dow-row">
                {DOW_SHORT.map((d) => <div key={d} className="ac-dow-cell">{d}</div>)}
              </div>
              <div className="ac-month-grid">
                {monthGridDays.map((dateObj) => {
                  const dateKey = dateKeyOf(dateObj);
                  const dayEvents = eventsByDate[dateKey] || [];
                  const isOutside = dateObj.getMonth() !== month - 1;
                  const isToday = isSameDay(dateObj, today);
                  const visible = dayEvents.slice(0, MAX_CHIPS_MONTH);
                  const overflowCount = dayEvents.length - visible.length;

                  return (
                    <div
                      key={dateKey}
                      className={`ac-month-cell ${isOutside ? "is-outside" : ""} ${isToday ? "is-today" : ""} ${dragOverDate === dateKey ? "is-drop-target" : ""}`}
                      onDragOver={(e) => handleDayDragOver(e, dateKey)}
                      onDragLeave={() => handleDayDragLeave(dateKey)}
                      onDrop={(e) => handleDayDrop(e, dateKey)}
                    >
                      <div className="ac-date-badge-row">
                        <span className="date-badge">{dateObj.getDate()}</span>
                      </div>
                      <div className="ac-month-cell-events">
                        {visible.map((ev) => renderChip(ev))}
                        {overflowCount > 0 && (
                          <button
                            className="ac-overflow-btn"
                            onClick={() => setGroupChooser({
                              label: `${dayEvents.length} inspections on ${MONTH_NAMES[dateObj.getMonth()]} ${dateObj.getDate()}`,
                              events: dayEvents
                            })}
                          >
                            +{overflowCount} more
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── WEEK VIEW ──────────────────────────────────────────────── */}
          {viewMode === "week" && (
            <>
              <div className="ac-week-header-row">
                {weekDays.map((dateObj) => {
                  const isToday = isSameDay(dateObj, today);
                  return (
                    <div key={dateKeyOf(dateObj)} className={`ac-week-col-header ${isToday ? "is-today" : ""}`}>
                      <span className="dow">{DOW_SHORT[dateObj.getDay()]}</span>
                      <span className="date-badge">{dateObj.getDate()}</span>
                    </div>
                  );
                })}
              </div>
              <div className="ac-week-grid">
                {weekDays.map((dateObj) => {
                  const dateKey = dateKeyOf(dateObj);
                  const dayEvents = eventsByDate[dateKey] || [];
                  return (
                    <div
                      key={dateKey}
                      className={`ac-week-col ${dragOverDate === dateKey ? "is-drop-target" : ""}`}
                      onDragOver={(e) => handleDayDragOver(e, dateKey)}
                      onDragLeave={() => handleDayDragLeave(dateKey)}
                      onDrop={(e) => handleDayDrop(e, dateKey)}
                    >
                      {dayEvents.length === 0
                        ? <div className="ac-week-col-empty">No inspections</div>
                        : dayEvents.map((ev) => renderChip(ev))}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── DAY VIEW ───────────────────────────────────────────────── */}
          {viewMode === "day" && (
            <div
              className={`ac-day-view ${dragOverDate === dateKeyOf(anchorDate) ? "is-drop-target" : ""}`}
              onDragOver={(e) => handleDayDragOver(e, dateKeyOf(anchorDate))}
              onDragLeave={() => handleDayDragLeave(dateKeyOf(anchorDate))}
              onDrop={(e) => handleDayDrop(e, dateKeyOf(anchorDate))}
            >
              {(eventsByDate[dateKeyOf(anchorDate)] || []).length === 0 ? (
                <div className="ac-day-view-empty">No inspections scheduled for this day.</div>
              ) : (
                (eventsByDate[dateKeyOf(anchorDate)] || []).map((ev) => renderDayCard(ev))
              )}
            </div>
          )}
        </div>

        <div className="ac-legend">
          {loading
            ? "Loading inspection schedule…"
            : `${events.length} customer inspection${events.length === 1 ? "" : "s"} scheduled ${viewMode === "month" ? "this month" : viewMode === "week" ? "this week" : "on this day"}.`}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 12, marginLeft: 16 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: "linear-gradient(135deg, #4f8ef7 0%, #1d5fe0 100%)", display: "inline-block" }} />
              Pending confirmation
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: "linear-gradient(135deg, #ffd54f 0%, #ffb300 100%)", display: "inline-block" }} />
              Confirmed
            </span>
          </span>
        </div>

        {/* Day overflow / multi-SO chooser — shown instead of the detail
            modal when a day has more inspections than Month view has room
            to show inline. */}
        {groupChooser && (
          <div className="ac-modal-backdrop" onClick={() => setGroupChooser(null)}>
            <div className="ac-modal" style={{ width: 420 }} onClick={(e) => e.stopPropagation()}>
              <h4>{groupChooser.label}</h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 4 }}>
                {groupChooser.events.map((ev) => (
                  <button
                    key={ev.soId}
                    onClick={() => openEvent(ev)}
                    style={{
                      textAlign: "left", padding: "10px 12px", borderRadius: 8,
                      border: "1px solid #e4e7ec", background: "#fff", cursor: "pointer"
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{ev.soId}</div>
                    <div style={{ fontSize: 13, color: "#667085" }}>{ev.customer}</div>
                  </button>
                ))}
              </div>
              <button className="ac-modal-close" onClick={() => setGroupChooser(null)}>Cancel</button>
            </div>
          </div>
        )}

        {selectedEvent && (
          <div className="ac-modal-backdrop" onClick={closeEvent}>
            <div className="ac-modal" onClick={(e) => e.stopPropagation()}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <h4 style={{ margin: 0 }}>{selectedEvent.soId}</h4>
                <span style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => setIsEditingModal((v) => !v)}
                    title={isEditingModal ? "Lock fields again" : "Unlock Proposed Date and Note for editing"}
                    style={{
                      padding: "8px 16px", borderRadius: 8, border: "1px solid #1a73e8",
                      background: isEditingModal ? "#eaf1fd" : "#fff", color: "#1a73e8",
                      fontWeight: 600, fontSize: 13, cursor: "pointer"
                    }}
                  >
                    {isEditingModal ? "Editing…" : "Edit"}
                  </button>
                  <button
                    onClick={handleConfirmInspection}
                    disabled={confirming}
                    style={{
                      padding: "8px 16px", borderRadius: 8, border: "none",
                      background: confirming ? "#93c5fd" : "#1a73e8", color: "#fff",
                      fontWeight: 600, fontSize: 13, cursor: confirming ? "default" : "pointer"
                    }}
                  >
                    {confirming ? "Confirming…" : "Confirm"}
                  </button>
                </span>
              </div>

              <div className="ac-modal-row">
                <span>Department</span>
                <span>{selectedEvent.proposedSource === "sales_order" ? "Sales" : "Quality"}</span>
              </div>

              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: "#667085", marginBottom: 4 }}>Proposed Date</div>
                <input
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  disabled={!isEditingModal}
                  style={{
                    width: "100%", border: "1px solid #d0d5dd", borderRadius: 8, padding: "8px 10px", fontSize: 13.5,
                    background: isEditingModal ? "#fff" : "#f9fafb", color: isEditingModal ? "#101828" : "#667085"
                  }}
                />
              </div>

              <div className="ac-modal-row">
                <span>Person Responsible</span>
                <span>{selectedEvent.assignedTo || "—"}</span>
              </div>

              <div className="ac-modal-row"><span>Customer</span><span>{selectedEvent.customer}</span></div>
              <div className="ac-modal-row"><span>Status</span><span>{selectedEvent.status || "—"}</span></div>
              <div className="ac-modal-row"><span>Planning Block</span><span>{selectedEvent.planningBlock || "—"}</span></div>

              <div className="ac-modal-row">
                <span>Confirmed</span>
                <span>{selectedEvent.confirmedDate || "Not confirmed"}</span>
              </div>

              {selectedEvent.items && selectedEvent.items.length > 0 && (
                <>
                  <div className="ac-modal-items-label">Products</div>
                  <table className="ac-modal-items-table">
                    <thead>
                      <tr>
                        <th>Product Code</th>
                        <th>Description</th>
                        <th>UOM</th>
                        <th>Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedEvent.items.map((item, idx) => (
                        <tr key={`${item.productCode}-${idx}`}>
                          <td>{item.productCode || "—"}</td>
                          <td>{item.description || "—"}</td>
                          <td>{item.uom || "—"}</td>
                          <td>{item.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              <div className="ac-modal-items-label">Note</div>
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="Add a note for this inspection…"
                rows={3}
                disabled={!isEditingModal}
                style={{
                  width: "100%", border: "1px solid #d0d5dd", borderRadius: 8, padding: "8px 10px", fontSize: 13.5, resize: "vertical",
                  background: isEditingModal ? "#fff" : "#f9fafb", color: isEditingModal ? "#101828" : "#667085"
                }}
              />

              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 22 }}>
                <button
                  onClick={handleSaveAll}
                  disabled={savingAll || !isEditingModal}
                  style={{
                    flex: 1, padding: "12px", borderRadius: 8, border: "1px solid #d0d5dd",
                    background: (!isEditingModal) ? "#f2f4f7" : "#fff",
                    color: (!isEditingModal) ? "#98a2b3" : "#344054",
                    fontWeight: 600, fontSize: 15, cursor: (savingAll || !isEditingModal) ? "default" : "pointer"
                  }}
                >
                  {savingAll ? "Saving…" : "Save"}
                </button>
                <button
                  className="ac-modal-close"
                  onClick={closeEvent}
                  style={{ width: "auto", flex: 1, marginTop: 0 }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default AuditCalendar;